import { Alert, Box, Button, CircularProgress, Snackbar, Switch, TextField, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useGetAllSettingsQuery, useUpdateSettingMutation } from "../../api/apiSlice";
import { AdminSetting } from "../../api/api.types";
import { ConfirmDialog } from "../../components/Dialogs/ConfirmDialog";
import { getApiErrorMessage } from "../../utils";

/** Group settings by category (prefix before the first dot). */
function groupSettings(settings: AdminSetting[]): Record<string, AdminSetting[]> {
  const groups: Record<string, AdminSetting[]> = {};
  for (const s of settings) {
    const category = s.key.split(".")[0];
    if (!groups[category]) groups[category] = [];
    groups[category].push(s);
  }
  return groups;
}

/** Format a category key like "custom_trial" → "Custom Trial". */
function formatCategory(key: string): string {
  return key
    .split("_")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Renders a single setting row with appropriate input for its type. */
const SettingRow: React.FC<{
  setting: AdminSetting;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}> = ({ setting, onSaved, onError }) => {
  const [updateSetting, { isLoading: isSaving }] = useUpdateSettingMutation();

  const serverValue =
    setting.type === "boolean" ? setting.value === "true" : setting.type === "number" ? Number(setting.value) : setting.value;

  const [draft, setDraft] = useState<string | number | boolean>(serverValue);

  useEffect(() => {
    setDraft(serverValue);
  }, [serverValue]);

  const isBoolean = setting.type === "boolean";
  const isNumber = setting.type === "number";

  const numericDraft = isNumber && String(draft).trim() !== "" ? Number(draft) : NaN;
  const min = setting.constraints?.min;
  const max = setting.constraints?.max;

  const draftIsValid = isBoolean
    ? true
    : isNumber
      ? String(draft).trim() !== "" &&
        Number.isFinite(numericDraft) &&
        (min === undefined || numericDraft >= min) &&
        (max === undefined || numericDraft <= max)
      : String(draft).length > 0;

  const draftDiffers = isBoolean ? draft !== serverValue : isNumber ? numericDraft !== serverValue : draft !== serverValue;

  const canSave = draftIsValid && draftDiffers && !isSaving;

  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleSave = async () => {
    if (isSaving) return;
    setConfirmOpen(false);
    const value = isBoolean ? draft : isNumber ? numericDraft : draft;
    try {
      await updateSetting({ key: setting.key, value: value as boolean | number | string }).unwrap();
      onSaved(`${setting.label} updated.`);
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, "Failed to update setting."));
    }
  };

  const confirmBody = isBoolean
    ? `Set "${setting.label}" from ${serverValue ? "enabled" : "disabled"} to ${draft ? "enabled" : "disabled"}?`
    : `Change "${setting.label}" from ${serverValue} to ${draft}?`;

  return (
    <Box sx={{ py: 2, "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "divider" } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box flex={1} mr={2}>
          <Typography variant="subtitle2" fontWeight={600}>
            {setting.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {setting.description}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1} minWidth={200} justifyContent="flex-end">
          {isBoolean ? (
            <Switch
              checked={draft as boolean}
              onChange={e => setDraft(e.target.checked)}
              disabled={isSaving}
              inputProps={{ "aria-label": setting.label }}
            />
          ) : (
            <TextField
              type={isNumber ? "number" : "text"}
              size="small"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              slotProps={isNumber ? { htmlInput: { min, max, step: "any" } } : undefined}
              error={!draftIsValid && String(draft).trim() !== ""}
              helperText={
                !draftIsValid && String(draft).trim() !== "" && isNumber && (min !== undefined || max !== undefined)
                  ? `Must be ${min !== undefined ? `≥${min}` : ""}${min !== undefined && max !== undefined ? " and " : ""}${
                      max !== undefined ? `≤${max}` : ""
                    }.`
                  : undefined
              }
              sx={{ width: 120 }}
            />
          )}
          <Button variant="contained" size="small" disabled={!canSave} onClick={() => setConfirmOpen(true)}>
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Box>
      {setting.updatedAt && (
        <Typography variant="caption" color="text.disabled" mt={0.5}>
          Last updated:{" "}
          {new Date(setting.updatedAt).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Typography>
      )}
      <ConfirmDialog
        open={confirmOpen}
        title="Confirm change"
        body={confirmBody}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleSave}
        loading={isSaving}
      />
    </Box>
  );
};

export const Settings: React.FC = () => {
  const { data, isLoading, isError } = useGetAllSettingsQuery();
  const [snack, setSnack] = useState<{ severity: "success" | "error"; message: string } | null>(null);

  const settingsData = data?.data;
  const grouped = useMemo(() => (settingsData ? groupSettings(settingsData) : {}), [settingsData]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box>
        <Alert severity="error">Failed to load settings. Please refresh the page.</Alert>
      </Box>
    );
  }

  return (
    <Box maxWidth={720}>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Tunable values that affect application behaviour. Changes take effect within ~1 minute across all servers.
      </Typography>

      {Object.entries(grouped).map(([category, settings]) => (
        <Box key={category} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 3, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            {formatCategory(category)}
          </Typography>
          {settings.map(s => (
            <SettingRow
              key={s.key}
              setting={s}
              onSaved={msg => setSnack({ severity: "success", message: msg })}
              onError={msg => setSnack({ severity: "error", message: msg })}
            />
          ))}
        </Box>
      ))}

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert onClose={() => setSnack(null)} severity={snack.severity} sx={{ width: "100%" }}>
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
